import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService, UserType } from '../services/auth.service';
import { Menu } from 'primeng/menu';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { MenuItem } from 'primeng/api';

interface User {
  username: string | null;
  email: string | null;
  name: string | null;
  userType: UserType;
}

declare const logout_redirect_uri: string;
declare const cognitoDomain: string;
declare const userPoolClientId: string;
declare const redirectSignIn: string;

@Component({
  selector: 'app-navbar-user',
  standalone: true,
  imports: [CommonModule, MenuModule, ButtonModule],
  template: `
    <div class="navbar-user-container" *ngIf="user?.name || user?.email">
      <p-button 
        [text]="true" 
        (click)="menu.toggle($event)"
        class="user-menu-button"
        severity="secondary">
        <i class="pi pi-user user-icon"></i>
        <span class="user-name">{{ user?.name || user?.email }}</span>
        <i class="pi pi-chevron-down dropdown-icon"></i>
      </p-button>
      
      <p-menu #menu [model]="menuItems" [popup]="true" styleClass="user-menu" appendTo="body">
        <ng-template pTemplate="item" let-item>
          <div class="menu-item-content" [class.logged-in-header]="item.isHeader">
            <i [class]="item.icon" *ngIf="item.icon"></i>
            <span>{{ item.label }}</span>
          </div>
        </ng-template>
      </p-menu>
    </div>
  `,
  styles: [
    `
    .navbar-user-container {
      display: flex;
      align-items: center;
      padding-right: 0.5rem;
    }
    
    ::ng-deep .user-menu-button {
      border: none !important;
      box-shadow: none !important;
      padding: 0.5rem 0.75rem !important;
      background: transparent !important;
      border-radius: 6px !important;
      transition: background-color 0.2s ease !important;
    }

    ::ng-deep .user-menu-button .p-button-label {
      display: flex !important;
      align-items: center !important;
      gap: 0.5rem !important;
      color: #333 !important;
      font-weight: 500 !important;
    }
    
    .user-icon {
      font-size: 1rem;
      color: #1f1a1aff;
    }
    
    .user-name {
      font-size: 0.95rem;
      color: #1f1a1aff;
    }
    
    .dropdown-icon {
      font-size: 0.75rem;
      color: #666;
      margin-left: 0.25rem;
    }
    
    ::ng-deep .user-menu {
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      border: 1px solid #e0e0e0;
      padding: 0.5rem 0;
    }
    
    ::ng-deep .user-menu .p-menu-list {
      padding: 0.5rem 1rem;
      display: flex;
      gap: 15px;
    }
    
    ::ng-deep .user-menu .p-menuitem {
      margin: 10px;
    }
    
    ::ng-deep .user-menu .p-menuitem-link {
      padding: 0.75rem 1rem;
      border-radius: 0;
      transition: background-color 0.2s ease;
    }
    
    ::ng-deep .user-menu .p-menuitem-link:hover {
      background-color: #f8f9fa;
    }
    
    .menu-item-content {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.9rem;
      cursor: pointer;
    }
    
    .menu-item-content.logged-in-header {
      color: #666;
      font-weight: 500;
      pointer-events: none;
    }
    
    ::ng-deep .user-menu .p-menuitem.logged-in-item .p-menuitem-link {
      cursor: default;
    }
    
    ::ng-deep .user-menu .p-menuitem.logged-in-item .p-menuitem-link:hover {
      background-color: transparent;
    }
    
    ::ng-deep .user-menu hr {
      margin: 0.5rem 0;
      border: none;
      border-top: 1px solid #e0e0e0;
    }
    
    .logout-item {
      color: #e74c3c !important;
    }
    
    ::ng-deep .user-menu .logout-menuitem .p-menuitem-link:hover {
      background-color: rgba(231, 76, 60, 0.1);
    }
    `
  ]
})
export class NavbarUserComponent implements OnInit {
  @ViewChild('menu') menu!: Menu;
  user: User | null = null;
  menuItems: MenuItem[] = [];

  constructor(private authService: AuthService) {
  }

  async ngOnInit() {
    const user = await this.authService.getUser();
    if(user) {
      this.user = {
        username: user.username as string | null,
        email: user.email as string | null,
        name: user.name as string | null,
        userType: user.userType || 'tenant'
      }
      this.initializeMenuItems();
    }
  }

  private initializeMenuItems() {
    this.menuItems = [
      {
        label: `Logged in as ${this.user?.userType || 'tenant'}`,
        isHeader: true,
        styleClass: 'logged-in-item',
      },
      {
        separator: true
      },
      {
        label: 'Change password',
        icon: 'pi pi-lock',
        command: () => this.changePassword()
      },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        styleClass: 'logout-menuitem',
        command: () => this.logout()
      }
    ];
  }

  changePassword() {
    const forgotPasswordUrl = `https://${cognitoDomain}/forgotPassword?` +
      `redirect_uri=${encodeURIComponent(redirectSignIn)}&` +
      `response_type=code&` +
      `client_id=${userPoolClientId}`;
    
    window.location.href = forgotPasswordUrl;
  }

  logout() {
    this.authService.logout().then(() => {
      localStorage.setItem('logoutEvent', 'manual');
      window.location.href = `https://${cognitoDomain}/logout?client_id=${userPoolClientId}&logout_uri=${logout_redirect_uri}`;
    })
  }
}
